// client/src/pages/sections/groups/SplitMemberButton.js
// Per-MemberCard "Change or Create New Signer Group" affordance. Sibling
// of MarkNlcButton. Opens a picker modal that lets the partner re-home
// the member by one of:
//
//   • Pick a ranked candidate SG (from /api/dgSgCandidates — derived
//     from the SG_SUGGESTED_BY_* clusters that include this client)
//   • Search for a different EXISTING SG anchor (GroupAnchorPicker over
//     the cached dgGroupAnchors list — only real SG parents)
//   • Make this client its own SG (self-referenced)
//
// Picking a candidate or typing a target posts split_to_other; the
// "own SG" choice posts split_to_self. Either way the modal closes
// on success and the parent's onResolved() refetches the panel.

import { useEffect, useRef, useState } from 'react';
import { loadAnchors, getAnchors } from './clientRosterCache';
import GroupAnchorPicker from './GroupAnchorPicker';
import { useOptimistic } from './optimisticContext';
import { emitToast, dismissToast, errorText } from './toast';
import { promptMigration } from './MigrationPromptHost';
import { postResolveAction, getActionPeriod } from './resolveActionQueue';
import ClientChip from './ClientChip';
import { useBringAlong, BringAlongFields } from './BringAlong';
import { useSubmitGuard } from './useSubmitGuard';

const CODE_RE = /(\d{5}-\d{3})/;

export default function SplitMemberButton({ member, onResolved, testMode, testSessionId, keepGroupVisible, assignMode, openSignal }) {
  // When the client has no signer group yet (e.g. Knuula-missing-SG), the
  // action is an ASSIGNMENT, not a move — there's nothing to move out of.
  // Swap the verbiage throughout the button + modal accordingly.
  const ACT    = assignMode ? 'Assign'    : 'Move';
  const acting = assignMode ? 'assigning' : 'moving';
  const [open, setOpen]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [sgAnchors, setSgAnchors]   = useState(() => (getAnchors()?.sgAnchors) || []);
  const [choice, setChoice]         = useState(null);  // 'candidate-{sg}' | 'manual' | 'self'
  const [manualSg, setManualSg]     = useState('');
  // Children = other active clients whose sg_parent_code = member.code.
  // When non-empty, the partner moving this client OUT of the group must
  // pick a successor (newSgParent) to anchor the remaining members.
  const [children, setChildren]         = useState([]);
  const [newSgParent, setNewSgParent]   = useState('');
  // Siblings = the other members of this client's own signer group (when it is a child). Leaving to a group of its
  // own offers them as "bring along" in THIS dialog, so the partner answers once — see BringAlong.js.
  const [siblings, setSiblings]         = useState([]);
  const bring = useBringAlong(siblings, member.entityType);
  const [submitting] = useState(false);   // optimistic flow closes modal; no live submitting toggle
  const guard = useSubmitGuard();         // one submit at a time: a double-click or a re-click mid-wait is ignored
  const [error, setError]           = useState(null);
  // Mixed-type-confirmation prediction for the currently-chosen target
  // (P1S3 parity with P1S2): 'smllc' or 'dependent' (at most one — D1: only the incoming Business is
  // confirmed). The partner must toggle it before submit; kept as a list so the payload shape is shared.
  const [mixedRequired,  setMixedRequired]  = useState([]);
  const [mixedConfirmed, setMixedConfirmed] = useState({});   // token -> true
  const [previewing,     setPreviewing]     = useState(false);
  // S3: a target that is itself a child of a group lands under that group's anchor — the preview
  // tells us when, so the partner sees the real group before confirming. previewError = the server's
  // reason when the target can't be used at all (unknown / inactive / No Longer Client).
  const [targetNote,     setTargetNote]     = useState(null);   // { from, to } | null
  const [previewError,   setPreviewError]   = useState(null);
  // A business is never the parent of a group an Individual joins: when the chosen target is business-anchored and this
  // client is a free Individual, the server's refusal carries an offer — make THIS client the parent instead and move
  // that group under it (move_clients_to_sg). `reverse` = the partner took the offer; reverseCodes = the group's clients.
  const [offer,          setOffer]          = useState(null);   // { parent, groupAnchor, clientCodes } | null
  const [reverse,        setReverse]        = useState(false);
  const [reverseCodes,   setReverseCodes]   = useState([]);
  const allMixedConfirmed = mixedRequired.every(t => mixedConfirmed[t]);
  const { dismissByClientCode, restoreIds, markPending, clearPending } = useOptimistic();
  // Tracks whether a backdrop click STARTED on the overlay. We only close on
  // a backdrop click when both the press and release land on the overlay —
  // otherwise a press that begins on a button (or anywhere in the modal) and
  // releases on the backdrop (e.g. the modal resized under the cursor) would
  // wrongly dismiss the modal instead of doing nothing / submitting.
  const overlayDownRef = useRef(false);

  // External open trigger: the row-level "Assign Signer Group" button bumps
  // `openSignal` to open this modal when the row is already expanded. Seed the
  // ref with the current value so a mount/remount (e.g. the drilldown opening)
  // does NOT auto-open — we react only when openSignal actually changes while
  // this component is mounted.
  const lastOpenSignalRef = useRef(openSignal);
  useEffect(() => {
    if (openSignal !== lastOpenSignalRef.current) {
      lastOpenSignalRef.current = openSignal;
      if (openSignal) setOpen(true);
    }
  }, [openSignal]);

  // Resolve the chosen choice → target SG code (or null for self / none).
  function resolveTargetCode() {
    if (choice === 'manual') {
      const m = manualSg.match(CODE_RE);
      return m ? m[1] : null;
    }
    if (choice && choice.startsWith('candidate-')) {
      return choice.slice('candidate-'.length);
    }
    return null;  // 'self' or unset → no target join, no mixed concern
  }

  // A different target ends any offer / choice that belonged to the previous one.
  useEffect(() => { setOffer(null); setReverse(false); setReverseCodes([]); }, [choice, manualSg, open]);

  // When the resolved target changes, ask the server whether the move
  // would create a confirmable mixed cohort. Self / no-target clears it.
  useEffect(() => {
    const target = resolveTargetCode();
    if (!open || !target || target === member.code) {
      setMixedRequired([]);
      setMixedConfirmed({});
      setTargetNote(null);
      setPreviewError(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewing(true);
    fetch(`/api/dgMovePreview?moving=${encodeURIComponent(member.code)}&target=${encodeURIComponent(target)}${reverse ? '&reverse=1' : ''}`,
      { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text();
          let msg = text;
          let offerBody = null;
          try { const j = JSON.parse(text); msg = j.error || text; offerBody = (j.offer && j.offer.makeParent) || null; } catch { /* plain-text body */ }
          const err = new Error(msg || `HTTP ${r.status}`);
          err.status = r.status;
          err.offer = offerBody;
          throw err;
        }
        return r.json();
      })
      .then((j) => {
        if (cancelled) return;
        setMixedRequired(Array.isArray(j.requiredConfirmations)
          ? j.requiredConfirmations
          : (j.requiredConfirmation ? [j.requiredConfirmation] : []));
        setMixedConfirmed({});   // re-confirm per target
        setTargetNote(j.retargetedFrom ? { from: j.retargetedFrom, to: j.resolvedTarget } : null);
        setReverseCodes(reverse && Array.isArray(j.clientCodes) ? j.clientCodes : []);
        setOffer(null);
        setPreviewError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setMixedRequired([]);
        setTargetNote(null);
        // A 400 is the server saying why this target can't be used; anything else is a transient
        // preview failure and the server re-validates at submit anyway.
        setPreviewError(e.status === 400 ? e.message : null);
        setOffer(!reverse && e.status === 400 ? (e.offer || null) : null);
      })
      .finally(() => { if (!cancelled) setPreviewing(false); });
    return () => { cancelled = true; };
    // manualSg + choice together determine the target; depend on both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, choice, manualSg, member.code, reverse]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting]);

  // Suggestion candidates fetch on modal open. Anchors come from the
  // shared cache (primed at section mount) — refetch only if cache empty.
  // Also fetch SG/RG context (dgNlcContext) to learn whether this client
  // is currently the parent of other active SG members; if so, the
  // partner must pick a successor before they can move out.
  useEffect(() => {
    if (!open || !member.code) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`/api/dgSgCandidates/${encodeURIComponent(member.code)}?period=${encodeURIComponent(getActionPeriod() || '')}`, { credentials: 'include' })
        .then(async (r) => {
          if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
          return r.json();
        }),
      loadAnchors({ fresh: true }),
      fetch(`/api/dgNlcContext/${encodeURIComponent(member.code)}`, { credentials: 'include' })
        .then(async (r) => {
          if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
          return r.json();
        }),
    ])
      .then(([candidatesRes, anchorsRes, ctxRes]) => {
        if (cancelled) return;
        setCandidates(candidatesRes.candidates || []);
        setSgAnchors(anchorsRes.sgAnchors || []);
        const kids = ctxRes.sgChildren || [];
        setChildren(kids);
        setSiblings(ctxRes.sgSiblings || []);
        // If there's exactly one eligible successor (not blocked because
        // they themselves anchor other clients), prefill it. Single-
        // option dropdowns are an unnecessary click.
        const eligible = kids.filter(c => !c.hasOwnFollowers);
        if (eligible.length === 1) {
          setNewSgParent(eligible[0].clientCode);
        }
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, member.code]);

  function close() {
    setOpen(false);
    setChoice(null);
    setManualSg('');
    setNewSgParent('');
    setError(null);
    bring.reset();
    setMixedRequired([]);
    setMixedConfirmed({});
    setTargetNote(null);
    setPreviewError(null);
  }

  // True when the clicked client currently anchors other active SG members
  // and a successor pick is mandatory before any of the three actions
  // (self / move-to-anchor / candidate) can be submitted.
  const isCurrentParent = children.length > 0;
  // "Make it its own group" by a CHILD with others left behind: ask who comes along here, not in a second dialog.
  const offerBringAlong = choice === 'self' && !isCurrentParent && siblings.length > 0;

  async function submit() {
    if (!choice) {
      setError('Pick where the client should go.');
      return;
    }
    // Mandatory successor pick when this client currently anchors others.
    // The backend re-validates, but checking here keeps the optimistic
    // dismiss from running on a request we know will fail.
    if (isCurrentParent && !reverse) {   // taking the "make me the parent" offer: this client stays the anchor
      if (!newSgParent) {
        setError('Pick a successor — one of this client\'s current Signer Group members must take over as the new anchor.');
        return;
      }
      if (!children.some(c => c.clientCode === newSgParent)) {
        setError('Successor must be one of the current Signer Group members.');
        return;
      }
    }
    const successorPayload = (isCurrentParent && !reverse) ? { newSgParent } : {};

    // Mixed-type gate: when the move would create a confirmable cohort
    // (predicted by dgMovePreview), the partner must toggle confirmation
    // first. 'self' never needs it (a 1-member SG can't be mixed).
    if (choice !== 'self' && previewError) {
      setError(previewError);
      return;
    }
    if (choice !== 'self' && mixedRequired.length && !allMixedConfirmed) {
      const missing = mixedRequired.filter(t => !mixedConfirmed[t]);
      setError(`Confirm ${missing.map(t => (t === 'smllc' ? 'the SMLLC arrangement' : 'the dependent arrangement')).join(' and ')} before ${acting} — toggle ${missing.length > 1 ? 'the confirmations' : 'the confirmation'} below.`);
      return;
    }
    const mixedPayload = (choice !== 'self' && mixedRequired.length)
      ? { mixedConfirmations: mixedRequired }
      : {};

    // Who comes along, answered in this same dialog (read BEFORE close() resets it). Every confirmation the selection
    // needs must be ticked; the server re-validates (move_clients_to_sg).
    if (offerBringAlong && !bring.ready) {
      setError('Check the confirmation below before bringing these clients along.');
      return;
    }
    const bringCodes   = offerBringAlong ? [...bring.selected] : [];
    const bringConfirm = offerBringAlong ? bring.requiredConfirm : [];

    let body;
    if (reverse && choice !== 'self' && reverseCodes.length) {
      body = {
        verb: 'move_clients_to_sg', clientCode: member.code,
        payload: { clientCodes: reverseCodes, ...mixedPayload },
        testMode: Boolean(testMode), sessionId: testMode ? testSessionId : null,
      };
    } else if (choice === 'self') {
      body = {
        verb: 'split_to_self', clientCode: member.code,
        payload: { ...successorPayload },
        testMode: Boolean(testMode), sessionId: testMode ? testSessionId : null,
      };
    } else if (choice === 'manual') {
      const codeMatch = manualSg.match(CODE_RE);
      if (!codeMatch) {
        setError('Pick a target Signer Group from the list.');
        return;
      }
      const target = codeMatch[1];
      if (target === member.code) {
        setError('Target Signer Group equals this client — use "Make own Signer Group" instead.');
        return;
      }
      // S3 (ruled 2026-10-26): a child client is not a valid target — only a live group anchor from the
      // picker list is. (The server also re-points a child to its anchor, as a backstop for a direct call.)
      if (!sgAnchors.some((a) => a.clientCode === target)) {
        setError(`${target} is not a Signer Group anchor, so a client cannot be moved into it. Pick a group from the list (a client that belongs to another group is not offered).`);
        return;
      }
      body = {
        verb: 'split_to_other', clientCode: member.code,
        payload: { targetSg: target, ...successorPayload, ...mixedPayload },
        testMode: Boolean(testMode), sessionId: testMode ? testSessionId : null,
      };
    } else if (choice.startsWith('candidate-')) {
      const targetSg = choice.slice('candidate-'.length);
      body = {
        verb: 'split_to_other', clientCode: member.code,
        payload: { targetSg, ...successorPayload, ...mixedPayload },
        testMode: Boolean(testMode), sessionId: testMode ? testSessionId : null,
      };
    } else {
      setError('Unknown choice.');
      return;
    }

    // Optimistic: mark the client pending for the whole action lifecycle so
    // every row referencing it shows the loading state; modal closes.
    // Normally we ALSO dismiss every issue tied to this client so it
    // disappears immediately. But inside a multi-member group
    // (keepGroupVisible), removing one member rarely resolves the whole
    // issue, so we keep the issue row visible (loading) and let the refetch
    // reconcile what's left instead of making the group vanish.
    // The clients coming along are hidden the same way (their issues regenerate on the server's revalidate), and so
    // are the clients of a business group that moves under this Individual (the "make me the parent" offer).
    const pendingCodes = [
      member.code,
      ...(body.verb === 'move_clients_to_sg' ? reverseCodes : []),
      ...bringCodes,
    ];
    markPending(pendingCodes);
    const hidden = keepGroupVisible ? [] : dismissByClientCode(member.code);
    const hiddenMoved = bringCodes.flatMap((c) => dismissByClientCode(c));
    close();
    const progressToastId = emitToast({
      kind: 'info',
      message: 'Calculating changes based on new groups…',
      durationMs: 0,
    });
    try {
      const data = await postResolveAction(body);
      // Follow-up: split_to_self on a child surfaces the other members
      // of the original SG as eligibleForMigration. If any exist, prompt
      // the partner to optionally bring some of them into the new SG.
      // Not when this dialog already asked (offerBringAlong): the partner has answered, including "nobody".
      const hasMigration = !offerBringAlong
        && data.verb === 'split_to_self'
        && Array.isArray(data.eligibleForMigration)
        && data.eligibleForMigration.length > 0;
      // The follow-up modal is hosted by <MigrationPromptHost/>, not by this button: the move drops this
      // member's card (and so this component) before the server answers. It opens as soon as the server has
      // answered, BEFORE the page refetch below: the refetch is the slow part (a 5-10 s wait with the partner
      // looking at nothing), and the modal needs nothing from it.
      if (hasMigration) {
        promptMigration({
          newSgAnchor: member.code, anchorEntityType: member.entityType,
          candidates: data.eligibleForMigration, onResolved, testMode, testSessionId,
        });
      }
      // The others the partner ticked move in right behind it, from the same click (same call as the follow-up
      // dialog makes). If that part fails the split itself stands: say so, and offer the follow-up dialog so they
      // can retry without redoing anything.
      if (bringCodes.length) {
        try {
          await postResolveAction({
            verb:       'move_clients_to_sg',
            clientCode: member.code,                  // dispatcher requires it
            payload:    {
              targetSg: member.code,
              clientCodes: bringCodes,
              ...(bringConfirm.length ? { mixedConfirmations: bringConfirm } : {}),
            },
            testMode:   Boolean(testMode),
            sessionId:  testMode ? testSessionId : null,
          });
        } catch (e2) {
          restoreIds(hiddenMoved);
          emitToast({
            kind: 'error',
            message: `${member.code} is now its own Signer Group, but the others couldn't be moved with it: ${errorText(e2)}. Choose them again to retry.`,
          });
          promptMigration({
            newSgAnchor: member.code, anchorEntityType: member.entityType,
            candidates: siblings.filter((s) => bringCodes.includes(s.clientCode)), onResolved, testMode, testSessionId,
          });
        }
      }
      if (onResolved) await onResolved(data);
    } catch (e) {
      restoreIds([...hidden, ...hiddenMoved]);
      emitToast({
        kind: 'error',
        message: `Couldn't ${ACT.toLowerCase()} ${member.code}: ${errorText(e)}. Restored.`,
      });
    } finally {
      clearPending(pendingCodes);
      dismissToast(progressToastId);
    }
  }

  return (
    <>
      <button
        type="button"
        className="cg-move-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={assignMode
          ? `Assign ${member.code} to a Signer Group, or make it its own Signer Group.`
          : `Move ${member.code} to another Signer Group, or make it its own Signer Group.`}
      >
        {assignMode ? 'Assign or Create New Signer Group' : 'Change or Create New Signer Group'}
      </button>

      {open && (
        <div
          className="cg-resolve-overlay"
          onMouseDown={(e) => { overlayDownRef.current = (e.target === e.currentTarget); }}
          onMouseUp={(e) => {
            const onBackdrop = overlayDownRef.current && e.target === e.currentTarget;
            overlayDownRef.current = false;
            if (onBackdrop && !submitting) close();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="cg-resolve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cg-resolve-header">
              <h3>{ACT} {member.code}</h3>
              <button type="button" className="cg-resolve-close"
                      disabled={submitting} onClick={close} aria-label="Close">×</button>
            </div>
            <div className="cg-resolve-summary">
              <div className="cg-resolve-summary-row">
                <ClientChip code={member.code} name={member.name} />
              </div>
            </div>

            <div className="cg-resolve-confirm">
              <p className="cg-resolve-confirm-body">Where should this client go?</p>
              <p className="cg-resolve-confirm-hint">
                Separating a client only changes its Signer Group — it can still be
                grouped with these clients at the Relationship Group level in Step 4.
              </p>

              {loading ? (
                <p className="cg-resolve-empty">Loading candidate Signer Groups…</p>
              ) : (
                <div className="cg-resolve-form">
                  {isCurrentParent && (
                    <fieldset className="cg-resolve-field cg-resolve-field--radio cg-nlc-section">
                      <legend className="cg-resolve-field-label">
                        <strong>{member.code}</strong> is currently the Signer Group parent of{' '}
                        {children.length} other member{children.length === 1 ? '' : 's'}.
                        Pick a successor — they will become the new Signer Group anchor for the
                        remaining members.
                        <span className="cg-resolve-required" aria-hidden="true"> *</span>
                      </legend>
                      <select
                        className="cg-nlc-subpicker"
                        value={newSgParent}
                        disabled={submitting}
                        onChange={(e) => setNewSgParent(e.target.value)}
                        style={{ marginTop: 4, width: '100%', maxWidth: 360 }}
                      >
                        <option value="">— pick a successor —</option>
                        {children.map((c) => {
                          // Children that already anchor their OWN followers
                          // are disabled — promoting them would silently merge
                          // those followers into this group.
                          const blocked = c.hasOwnFollowers;
                          return (
                            <option
                              key={c.clientCode}
                              value={c.clientCode}
                              disabled={blocked}
                              title={blocked
                                ? `Already anchors ${c.ownFollowerCount} other member${c.ownFollowerCount === 1 ? '' : 's'} — promoting would merge groups.`
                                : undefined}
                            >
                              {c.clientCode}{c.clientName ? ` — ${c.clientName}` : ''}
                              {blocked ? ` (anchors ${c.ownFollowerCount} other${c.ownFollowerCount === 1 ? '' : 's'} — cannot promote)` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </fieldset>
                  )}
                  {candidates.length > 0 && (
                    <fieldset className="cg-resolve-field cg-resolve-field--radio">
                      <legend className="cg-resolve-field-label">
                        Suggested Signer Groups
                        <span className="cg-candidate-hover-hint"> — hover to view existing group structure</span>
                      </legend>
                      {candidates.map((c) => {
                        const id = `candidate-${c.sgParentCode}`;
                        const members = Array.isArray(c.members) ? c.members : [];
                        return (
                          <label key={id} className="cg-resolve-radio-row cg-candidate-hoverable">
                            <input
                              type="radio" name="splitChoice" value={id}
                              checked={choice === id}
                              disabled={submitting}
                              onChange={() => setChoice(id)}
                            />
                            <span className="cg-candidate-row">
                              <ClientChip code={c.sgParentCode} name={c.parentName} />
                              <span className="cg-candidate-meta">
                                {' '}· {c.evidence.map((e) => `${e.memberCount} share ${e.basis}`).join('; ')}
                              </span>
                              {members.length > 0 && (
                                <span className="cg-candidate-grouptag">
                                  ▾ {members.length} in group
                                </span>
                              )}
                            </span>
                            {/* Hover reveals the whole signer group — parent +
                                children, code + name — so the partner can see
                                what they're moving into without leaving the
                                modal. Lives inside the hovered label so the
                                cursor stays over it while it's open. */}
                            {members.length > 0 && (
                              <div className="cg-candidate-members" role="group"
                                   aria-label={`Members of Signer Group ${c.sgParentCode}`}>
                                {members.map((m) => (
                                  <div key={m.code} className="cg-candidate-member">
                                    <ClientChip code={m.code} name={m.name} />
                                    {m.isParent && <span className="cg-candidate-member-parent"> (parent)</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </label>
                        );
                      })}
                    </fieldset>
                  )}

                  <fieldset className="cg-resolve-field cg-resolve-field--radio">
                    <legend className="cg-resolve-field-label">
                      {candidates.length > 0 ? 'Or pick a different Signer Group' : 'Pick the target Signer Group'}
                    </legend>
                    <label className="cg-resolve-radio-row">
                      <input
                        type="radio" name="splitChoice" value="manual"
                        checked={choice === 'manual'} disabled={submitting}
                        onChange={() => setChoice('manual')}
                      />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {ACT} to Signer Group
                        <GroupAnchorPicker
                          anchors={sgAnchors}
                          value={manualSg}
                          disabled={submitting}
                          onChange={(v) => { setManualSg(v); setChoice('manual'); }}
                          onFocus={() => setChoice('manual')}
                        />
                      </span>
                    </label>
                    <label className="cg-resolve-radio-row">
                      <input
                        type="radio" name="splitChoice" value="self"
                        checked={choice === 'self'} disabled={submitting}
                        onChange={() => setChoice('self')}
                      />
                      <span>Make {member.code} its own Signer Group (self-referenced)</span>
                    </label>
                  </fieldset>
                </div>
              )}

              {/* S3: the target is a member of another group — the move lands under that group's anchor. */}
              {choice !== 'self' && targetNote && (
                <p className="cg-resolve-confirm-hint" role="note">
                  <ClientChip code={targetNote.from} /> is itself a member of Signer Group <ClientChip code={targetNote.to} />,
                  so <ClientChip code={member.code} name={member.name} /> will join that group under its anchor <ClientChip code={targetNote.to} />.
                </p>
              )}
              {choice !== 'self' && previewError && (
                <div className="cg-resolve-error" role="alert">{previewError}</div>
              )}
              {choice !== 'self' && previewError && offer && !reverse && (
                <p className="cg-resolve-confirm-hint" role="note">
                  Instead, make <code>{member.code}</code> the parent:{' '}
                  Signer Group <code>{offer.groupAnchor}</code> ({offer.clientCodes.length} client{offer.clientCodes.length === 1 ? '' : 's'}) moves under it.{' '}
                  <button type="button" className="cg-btn cg-btn--secondary" disabled={submitting || previewing}
                          onClick={() => setReverse(true)}>
                    Make {member.code} the parent instead
                  </button>
                </p>
              )}
              {choice !== 'self' && reverse && !previewError && reverseCodes.length > 0 && (
                <p className="cg-resolve-confirm-hint" role="note">
                  <code>{member.code}</code> becomes the parent of Signer Group {targetNote ? targetNote.to : (resolveTargetCode() || '')}:
                  its {reverseCodes.length} client{reverseCodes.length === 1 ? '' : 's'} will move under {member.code}.{' '}
                  <button type="button" className="cg-btn cg-btn--secondary" disabled={submitting || previewing}
                          onClick={() => setReverse(false)}>Cancel this</button>
                </p>
              )}

              {/* Mixed-type confirmation toggle — the confirmation the chosen target needs: a Business +
                  Individual/Estate/Trust group (SMLLC) or two or more Individuals (dependent). Only the
                  incoming client is confirmed (D1). The partner must toggle it before the move is
                  allowed (server re-validates). */}
              {choice !== 'self' && mixedRequired.map((token) => (
                <div key={token} className="cg-resolve-field cg-resolve-field--toggle">
                  <div className="cg-resolve-toggle-row">
                    <div className="cg-resolve-toggle-text">
                      {token === 'smllc'
                        ? "This move creates a Business + Individual/Estate/Trust Signer Group, or an Estate/Trust with an Individual. Confirm the Business is a single-member LLC on that personal return, or that the Estate/Trust files with the Individual."
                        : "This move creates a Signer Group with two or more Individuals. Confirm the additional Individual is a dependent minor on the other Individual's return."}
                      <span className="cg-resolve-required" aria-hidden="true"> *</span>
                    </div>
                    <label className={`cg-resolve-toggle ${submitting ? 'disabled' : ''}`} aria-label={`Confirm ${token}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(mixedConfirmed[token])}
                        disabled={submitting}
                        onChange={(e) => setMixedConfirmed((prev) => ({ ...prev, [token]: e.target.checked }))}
                      />
                      <span className="cg-resolve-toggle-slider" />
                    </label>
                  </div>
                </div>
              ))}

              {/* A child leaving to a group of its own: who else comes along, asked here so there is no second dialog. */}
              {offerBringAlong && (
                <BringAlongFields anchorCode={member.code} candidates={siblings} bring={bring} disabled={submitting} />
              )}

              {error && (
                <div className="cg-resolve-error" role="alert">
                  <strong>Couldn't {ACT.toLowerCase()}:</strong> {error}
                </div>
              )}

              <div className="cg-resolve-actions">
                <button type="button" className="cg-btn cg-btn--secondary"
                        disabled={submitting} onClick={close}>Cancel</button>
                <button type="button" className="cg-btn cg-btn--primary"
                        disabled={submitting || loading || previewing
                          || (choice !== 'self' && ((mixedRequired.length > 0 && !allMixedConfirmed) || Boolean(previewError)))
                          || (offerBringAlong && !bring.ready)}
                        onClick={() => guard(submit)}>
                  {submitting ? 'Working…' : previewing ? 'Checking…'
                    : offerBringAlong && bring.selected.size > 0
                      ? `${ACT} with ${bring.selected.size} other${bring.selected.size === 1 ? '' : 's'}`
                      : ACT}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
